import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { toast } from 'react-toastify';
import { LuCheck, LuFileText, LuLoaderCircle, LuLock, LuScrollText, LuSend } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '../../../components/form/FormControls';
import { auth, db, storage } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { BLOCK_TITLE, CARD, EmptyState, LoadingRows, SectionHeader } from '../components/ProfileUi';
import ComplaintCard from './ComplaintCard';
import { formatTurkishDate, generateDilekceDocument, generateDocumentNumber } from './complaintDocx';
import { COMPLAINT_TYPES, getComplaintTargets, toDate } from './complaintOptions';

/**
 * "Dilekçelerim": write a petition to the people in charge (it's saved with
 * a DOCX copy) and follow the ones sent before, with their replies.
 */
const ComplaintsSection = ({ userData }) => {
  const { t, locale } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const label = (key, vars) => t(`profilePage.complaints.form.${key}`, vars);
  const complaintTargets = getComplaintTargets();

  const [complaintType, setComplaintType] = useState('');
  const [complaintSubject, setComplaintSubject] = useState('');
  const [complaintDetails, setComplaintDetails] = useState('');
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [complaintLoading, setComplaintLoading] = useState(false);
  const [myComplaints, setMyComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);

  const fetchMyComplaints = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    setComplaintsLoading(true);
    try {
      // orderBy yerine istemcide sıralama (bileşik dizin gerekmesin)
      const querySnapshot = await getDocs(query(collection(db, 'Complaints'), where('uid', '==', user.uid)));
      const complaints = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      complaints.sort((a, b) => toDate(b.createdAt) - toDate(a.createdAt));
      setMyComplaints(complaints);
    } catch (error) {
      console.error('Error fetching complaints: ', error);
      toast.error(tRef.current('profilePage.complaints.toast.historyError'));
    } finally {
      setComplaintsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyComplaints();
  }, [fetchMyComplaints]);

  const toggleTarget = (email) => {
    setSelectedTargets((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));
  };

  const handleComplaintSubmit = async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error(label('errors.session'));
      return;
    }
    if (!complaintType) {
      toast.error(label('errors.type'));
      return;
    }
    if (!complaintSubject || !complaintDetails) {
      toast.error(label('errors.content'));
      return;
    }
    if (selectedTargets.length === 0) {
      toast.error(label('errors.target'));
      return;
    }

    const targetNames = selectedTargets.map((email) => complaintTargets.find((target) => target.email === email)?.name || email);

    setComplaintLoading(true);
    try {
      const documentNumber = generateDocumentNumber();
      const formattedDate = formatTurkishDate(new Date());
      const employeeName = userData.eName && userData.eSurname ? `${userData.eName} ${userData.eSurname}` : (user.displayName || user.email);
      const employeeEmail = userData.eMail || user.email;
      const typeLabel = COMPLAINT_TYPES.find((type) => type.value === complaintType)?.label || complaintType;

      const docxBlob = await generateDilekceDocument({
        documentNumber,
        formattedDate,
        employeeName,
        employeeEmail,
        employeeTitle: userData.eTitle || '',
        employeeDepartment: userData.eDepartment || '',
        subject: complaintSubject,
        details: complaintDetails,
        typeLabel,
        targetNames,
      });

      const fileName = `dilekce_${documentNumber}.docx`;
      const storageRef = ref(storage, `dilekce/${user.uid}/${fileName}`);
      await uploadBytes(storageRef, docxBlob);
      const downloadUrl = await getDownloadURL(storageRef);

      await addDoc(collection(db, 'Complaints'), {
        uid: user.uid,
        documentNumber,
        employeeName,
        employeeEmail,
        employeeTitle: userData.eTitle || '',
        employeeDepartment: userData.eDepartment || '',
        subject: complaintSubject,
        details: complaintDetails,
        type: complaintType,
        typeLabel,
        targetEmails: selectedTargets,
        targetNames,
        status: 'PENDING',
        createdAt: new Date(),
        docxUrl: downloadUrl,
        docxFileName: fileName,
      });

      toast.success(label('sent'));
      setComplaintSubject('');
      setComplaintDetails('');
      setComplaintType('');
      setSelectedTargets([]);
      fetchMyComplaints();
    } catch (error) {
      console.error('Error submitting dilekce: ', error);
      toast.error(label('errors.send', { message: error.message }));
    } finally {
      setComplaintLoading(false);
    }
  };

  const fullName = userData.eName && userData.eSurname ? `${userData.eName} ${userData.eSurname}` : '';

  return (
    <div className="tw-space-y-6">
      <SectionHeader
        icon={LuScrollText}
        gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30"
        title={label('title')}
        description={label('description')}
      />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleComplaintSubmit();
        }}
        className={`${CARD} tw-space-y-5 tw-p-4 sm:tw-p-6`}
      >
        {/* Antet */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-slate-200 tw-pb-4 dark:tw-border-white/10">
          <span className="tw-text-sm tw-font-bold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">HabcareCRO</span>
          <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{new Date().toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </div>
        <div className="tw-text-center">
          <p className="tw-text-xl tw-font-bold tw-tracking-[0.2em] tw-text-slate-900 dark:tw-text-white">{label('heading')}</p>
          <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('subheading')}</p>
        </div>

        {/* Muhatap */}
        <fieldset className="tw-space-y-2">
          <legend className={`${BLOCK_TITLE} tw-mb-2`}>{label('targets')} <span aria-hidden="true" className="tw-text-rose-500">*</span></legend>
          <div className="tw-grid tw-gap-2 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
            {complaintTargets.map((target) => {
              const selected = selectedTargets.includes(target.email);
              return (
                <button
                  key={target.email}
                  type="button"
                  role="checkbox"
                  aria-checked={selected}
                  onClick={() => toggleTarget(target.email)}
                  className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-px-3.5 tw-py-3 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                    selected
                      ? 'tw-border-teal-500/50 tw-bg-teal-500/[0.08] dark:tw-border-teal-400/40 dark:tw-bg-teal-400/10'
                      : `tw-bg-white/70 hover:tw-bg-white dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06] ${target.isTest ? 'tw-border-dashed tw-border-amber-400/60' : 'tw-border-slate-200 dark:tw-border-white/10'}`
                  }`}
                >
                  <span aria-hidden="true" className={`tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-md tw-border-2 ${selected ? 'tw-border-teal-500 tw-bg-teal-500 tw-text-white' : 'tw-border-slate-300 dark:tw-border-white/20'}`}>
                    {selected && <LuCheck className="tw-h-3.5 tw-w-3.5" strokeWidth={3} />}
                  </span>
                  <span className="tw-min-w-0">
                    <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{target.name}</span>
                    <span className="tw-block tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{target.isTest ? label('testMode') : label('authority')}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Başvuru sahibi */}
        <div className="tw-space-y-2">
          <p className={BLOCK_TITLE}>{label('sender')}</p>
          <dl className="tw-grid tw-gap-x-6 tw-gap-y-1 tw-rounded-2xl tw-bg-slate-500/[0.05] tw-px-4 tw-py-3 tw-text-sm sm:tw-grid-cols-2">
            <div className="tw-flex tw-gap-2"><dt className="tw-text-slate-500 dark:tw-text-slate-400">{label('name')}:</dt><dd className="tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{fullName || label('loading')}</dd></div>
            <div className="tw-flex tw-min-w-0 tw-gap-2"><dt className="tw-text-slate-500 dark:tw-text-slate-400">{label('email')}:</dt><dd className="tw-truncate tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{userData.eMail || auth.currentUser?.email || ''}</dd></div>
            {userData.eTitle && <div className="tw-flex tw-gap-2"><dt className="tw-text-slate-500 dark:tw-text-slate-400">{label('title2')}:</dt><dd className="tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{userData.eTitle}</dd></div>}
            {userData.eDepartment && <div className="tw-flex tw-gap-2"><dt className="tw-text-slate-500 dark:tw-text-slate-400">{label('department')}:</dt><dd className="tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{userData.eDepartment}</dd></div>}
          </dl>
        </div>

        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('type')} htmlFor="complaint-type" required>
            <SelectInput
              id="complaint-type"
              value={complaintType}
              onChange={(event) => setComplaintType(event.target.value)}
              placeholder={label('typePlaceholder')}
              options={COMPLAINT_TYPES.map((type) => ({ value: type.value, label: t(`profilePage.complaints.types.${type.value}`) }))}
            />
          </Field>
          <Field label={label('subject')} htmlFor="complaint-subject" required>
            <TextInput id="complaint-subject" value={complaintSubject} onChange={(event) => setComplaintSubject(event.target.value)} placeholder={label('subjectPlaceholder')} />
          </Field>
        </div>

        <div className="tw-space-y-2">
          <Field label={label('details')} htmlFor="complaint-details" required>
            <div className="tw-space-y-2 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/50 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
              <p className="tw-text-sm tw-italic tw-text-slate-500 dark:tw-text-slate-400">{label('greeting')}</p>
              <TextArea id="complaint-details" rows={8} value={complaintDetails} onChange={(event) => setComplaintDetails(event.target.value)} placeholder={label('detailsPlaceholder')} className="tw-resize-y" />
              <div className="tw-text-right tw-text-sm tw-italic tw-text-slate-500 dark:tw-text-slate-400">
                <p className="tw-block">{label('closing1')}</p>
                <p className="tw-block">{label('closing2')}</p>
                {fullName && <p className="tw-mt-1 tw-font-semibold tw-not-italic tw-text-slate-800 dark:tw-text-slate-100">{fullName}</p>}
              </div>
            </div>
          </Field>
        </div>

        <div className="tw-flex tw-items-start tw-gap-2.5 tw-rounded-2xl tw-bg-slate-900/[0.04] tw-px-3.5 tw-py-3 tw-text-sm tw-text-slate-600 dark:tw-bg-white/[0.04] dark:tw-text-slate-300">
          <LuLock aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
          {label('privacy')}
        </div>

        <div className="tw-flex tw-justify-end">
          <Button variant="primary" type="submit" disabled={complaintLoading} className="max-sm:tw-w-full">
            {complaintLoading ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSend aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {complaintLoading ? label('sending') : label('submit')}
          </Button>
        </div>
      </form>

      {/* Geçmiş dilekçeler */}
      <section className="tw-space-y-3">
        <div className="tw-min-w-0">
          <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">
            <LuFileText aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
            {t('profilePage.complaints.history.title')}
          </h3>
          <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{t('profilePage.complaints.history.description')}</p>
        </div>
        {complaintsLoading && <LoadingRows count={2} height="tw-h-28" />}
        {!complaintsLoading && myComplaints.length === 0 && <EmptyState compact icon={LuFileText} title={t('profilePage.complaints.history.empty')} />}
        {!complaintsLoading && myComplaints.length > 0 && (
          <ul className="tw-space-y-3">
            {myComplaints.map((complaint) => <ComplaintCard key={complaint.id} complaint={complaint} />)}
          </ul>
        )}
      </section>
    </div>
  );
};

ComplaintsSection.propTypes = { userData: PropTypes.object.isRequired };

export default ComplaintsSection;
